import {
  STORE_PREFERENCE_OPTIONS,
  type StorePreferenceOption,
} from "./storePreferences";

type StoreIntentMode = "none" | "supported" | "unsupported_only";
type StoreContextKeyword = "at" | "from" | "near";

export type ParsedStoreIntent =
  | {
      mode: "none";
      storeIds: [];
    }
  | {
      mode: "supported";
      storeIds: string[];
    }
  | {
      mode: "unsupported_only";
      storeIds: [];
    };

type StoreAlias = {
  storeId: string;
  normalized: string;
  compact: string;
  pattern: RegExp;
};

type UnsupportedStoreAlias = {
  normalized: string;
  compact: string;
  pattern: RegExp;
};

const STORE_CONTEXT_PATTERN = /\b(at|from|near)\b\s+(.+?)(?=\b(?:at|from|near)\b|$)/gi;
const CONTEXT_SEGMENT_SPLIT_PATTERN = /\b(?:and|or)\b|[,/+&]/gi;
const LEADING_ARTICLE_PATTERN = /^(?:the|a|an)\s+/i;
const IGNORED_CONTEXT_MENTIONS = new Set([
  "me",
  "my",
  "here",
  "there",
  "nearby",
  "home",
  "house",
  "work",
  "office",
  "downtown",
  "uptown",
  "neighborhood",
]);
const EXPLICIT_UNSUPPORTED_STORE_ALIASES = [
  // Target was dropped as a searchable chain on 2026-09-15. It stays in this
  // vocabulary so "milk at target" is still recognised as a store mention:
  // the word is stripped from the product query and the shopper is told the
  // chain is not searched, rather than us searching for a product called
  // "milk target".
  "target",
  "cvs",
  "walgreens",
  "rite aid",
  "dollar tree",
  "family dollar",
  "7-eleven",
  "7 eleven",
];

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeMention = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const compactMention = (value: string): string => normalizeMention(value).replace(/\s+/g, "");

const buildStoreAliases = (option: StorePreferenceOption): StoreAlias[] => {
  const aliasCandidates = [
    option.name,
    option.id,
    option.id.replace(/_/g, " "),
    option.name.replace(/&/g, " and "),
  ];
  const seen = new Set<string>();

  return aliasCandidates
    .map((candidate) => normalizeMention(candidate))
    .filter((candidate) => {
      if (!candidate || seen.has(candidate)) {
        return false;
      }
      seen.add(candidate);
      return true;
    })
    .map((normalized) => ({
      storeId: option.id,
      normalized,
      compact: normalized.replace(/\s+/g, ""),
      pattern: new RegExp(`\\b${escapeRegex(normalized)}\\b`),
    }));
};

const STORE_ALIASES = STORE_PREFERENCE_OPTIONS.flatMap((option) => buildStoreAliases(option));
const UNSUPPORTED_STORE_ALIASES: UnsupportedStoreAlias[] = EXPLICIT_UNSUPPORTED_STORE_ALIASES.map(
  (alias) => {
    const normalized = normalizeMention(alias);
    return {
      normalized,
      compact: normalized.replace(/\s+/g, ""),
      pattern: new RegExp(`\\b${escapeRegex(normalized)}\\b`),
    };
  },
);

const resolveSupportedStoreId = (candidate: string): string | undefined => {
  const normalizedCandidate = normalizeMention(candidate);
  const compactCandidate = compactMention(candidate);
  if (!normalizedCandidate || !compactCandidate) {
    return undefined;
  }

  for (const alias of STORE_ALIASES) {
    if (alias.pattern.test(normalizedCandidate)) {
      return alias.storeId;
    }
    if (compactCandidate === alias.compact) {
      return alias.storeId;
    }
    if (alias.normalized.includes(" ") && compactCandidate.startsWith(alias.compact)) {
      return alias.storeId;
    }
  }

  return undefined;
};

const hasExplicitUnsupportedStoreMention = (candidate: string): boolean => {
  const normalizedCandidate = normalizeMention(candidate);
  const compactCandidate = compactMention(candidate);
  if (!normalizedCandidate || !compactCandidate) {
    return false;
  }

  for (const alias of UNSUPPORTED_STORE_ALIASES) {
    if (alias.pattern.test(normalizedCandidate)) {
      return true;
    }
    if (compactCandidate === alias.compact) {
      return true;
    }
    if (alias.normalized.includes(" ") && compactCandidate.startsWith(alias.compact)) {
      return true;
    }
  }

  return false;
};

const extractContextMentions = (
  rawQuery: string,
): Array<{ keyword: StoreContextKeyword; mention: string }> => {
  const matches: Array<{ keyword: StoreContextKeyword; mention: string }> = [];
  const normalizedQuery = rawQuery.trim();
  if (!normalizedQuery) {
    return matches;
  }

  const contextRegex = new RegExp(STORE_CONTEXT_PATTERN.source, "gi");
  let result = contextRegex.exec(normalizedQuery);
  while (result) {
    const rawKeyword = normalizeMention(result[1]) as StoreContextKeyword;
    const rawSegment = result[2] || "";
    rawSegment
      .split(CONTEXT_SEGMENT_SPLIT_PATTERN)
      .map((part) => part.replace(LEADING_ARTICLE_PATTERN, "").trim())
      .forEach((part) => {
        const normalizedPart = normalizeMention(part);
        if (!normalizedPart) {
          return;
        }
        if (IGNORED_CONTEXT_MENTIONS.has(normalizedPart)) {
          return;
        }
        matches.push({
          keyword: rawKeyword,
          mention: part,
        });
      });

    result = contextRegex.exec(normalizedQuery);
  }

  return matches;
};

export const parseStoreIntentFromQuery = (rawQuery: string): ParsedStoreIntent => {
  const contextMentions = extractContextMentions(rawQuery);
  if (contextMentions.length === 0) {
    return {
      mode: "none",
      storeIds: [],
    };
  }

  const supportedStoreIds: string[] = [];
  const seenStoreIds = new Set<string>();
  let hasExplicitUnsupportedStore = false;

  contextMentions.forEach((mention) => {
    const matchedStoreId = resolveSupportedStoreId(mention.mention);
    if (matchedStoreId) {
      if (!seenStoreIds.has(matchedStoreId)) {
        seenStoreIds.add(matchedStoreId);
        supportedStoreIds.push(matchedStoreId);
      }
      return;
    }

    if (hasExplicitUnsupportedStoreMention(mention.mention)) {
      hasExplicitUnsupportedStore = true;
    }
  });

  if (supportedStoreIds.length > 0) {
    return {
      mode: "supported",
      storeIds: supportedStoreIds,
    };
  }

  if (hasExplicitUnsupportedStore) {
    return {
      mode: "unsupported_only",
      storeIds: [],
    };
  }

  return {
    mode: "none",
    storeIds: [],
  };
};

/**
 * Removes "at/from/near <store>" spans whose every non-ignored part is a
 * supported store or an explicitly unsupported chain, so a product query such
 * as "milk at walmart" becomes "milk". Segments that mention anything else
 * ("from scratch", "near me", "near 60616", "at meijer") are left untouched
 * because they are not store filters. Consumed by the server-side deterministic
 * parser; parseStoreIntentFromQuery is unchanged.
 */
export const stripStoreContextMentions = (rawQuery: string): string => {
  const normalizedQuery = rawQuery.trim();
  if (!normalizedQuery) {
    return "";
  }

  const contextRegex = new RegExp(STORE_CONTEXT_PATTERN.source, "gi");
  let output = "";
  let lastIndex = 0;
  let removedAny = false;
  let result = contextRegex.exec(normalizedQuery);
  while (result) {
    const rawSegment = result[2] || "";
    const parts = rawSegment
      .split(CONTEXT_SEGMENT_SPLIT_PATTERN)
      .map((part) => part.replace(LEADING_ARTICLE_PATTERN, "").trim())
      .map((part) => normalizeMention(part))
      .filter((part) => part && !IGNORED_CONTEXT_MENTIONS.has(part));
    const isStoreOnlySegment =
      parts.length > 0 &&
      parts.every(
        (part) => Boolean(resolveSupportedStoreId(part)) || hasExplicitUnsupportedStoreMention(part),
      );

    if (isStoreOnlySegment) {
      output += normalizedQuery.slice(lastIndex, result.index);
      lastIndex = result.index + result[0].length;
      removedAny = true;
    }

    result = contextRegex.exec(normalizedQuery);
  }

  if (!removedAny) {
    return rawQuery;
  }

  output += normalizedQuery.slice(lastIndex);
  return output.replace(/\s+/g, " ").trim();
};

export const getStoreIntentMode = (rawQuery: string): StoreIntentMode =>
  parseStoreIntentFromQuery(rawQuery).mode;
